import React, { useMemo, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDraggable } from '@dnd-kit/core';
import { motion, AnimatePresence, animate, useMotionValue } from 'framer-motion';
import { MessageSquare, CheckSquare, Check, Hourglass } from 'lucide-react';
import { PriorityBadge, AsignadosStack, HitoChip } from '../utils/displays';
import {
  RESORTE_TARJETA, RESORTE_ALZAR, RESORTE_HUECO,
  ESCALA_ALZADA, SOMBRA_NULA, SOMBRA_ALZADA,
} from '../utils/motion';

// --- Chip de fecha de vencimiento ---
// `neutra`: tarea hecha/cancelada o en espera (su plazo no corre): la fecha se
// muestra en gris, sin alarma. El chip "En espera" de arriba ya lo explica.
const FechaChip = ({ fecha, neutra = false }) => {
  const info = useMemo(() => {
    if (!fecha) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(fecha + 'T00:00:00');
    const diff = Math.floor((d - today) / (24 * 60 * 60 * 1000));

    const label = (() => {
      if (diff === 0) return 'Hoy';
      if (diff === 1) return 'Mañana';
      if (diff === -1) return 'Ayer';
      if (diff < 0) return d.toLocaleDateString('es-VE', { day: 'numeric', month: 'short' });
      if (diff < 7) return d.toLocaleDateString('es-VE', { weekday: 'short' }).replace('.', '');
      return d.toLocaleDateString('es-VE', { day: 'numeric', month: 'short' });
    })();

    let cls = 'text-slate-400';
    if (neutra) cls = 'text-slate-400';
    else if (diff < 0) cls = 'text-rose-600 font-medium';
    else if (diff <= 2) cls = 'text-amber-600 font-medium';

    return { label, cls };
  }, [fecha, neutra]);

  if (!info) return null;

  return (
    <span className={`text-[10px] tabular-nums ${info.cls}`}>
      {info.label}
    </span>
  );
};

const EtiquetaChip = ({ etiqueta }) => (
  <span
    title={etiqueta.nombre}
    className="inline-flex items-center gap-1 pl-1.5 pr-1.5 py-px rounded text-[9px] font-medium leading-none max-w-[100px]"
    style={{
      backgroundColor: `${etiqueta.color || '#94a3b8'}1f`,
      color: etiqueta.color || '#475569',
    }}
  >
    <span className="w-1 h-1 rounded-full flex-shrink-0" style={{ backgroundColor: etiqueta.color || '#94a3b8' }} />
    <span className="truncate">{etiqueta.nombre}</span>
  </span>
);

// --- Render visual puro de la card. No registra useSortable, así puede usarse:
//     1) Dentro del TaskCard "real" (que sí usa useSortable)
//     2) Dentro del DragOverlay sin generar un segundo registro con el mismo id
//        (evita el offset de cursor que aparecía antes)
const TaskCardBody = ({ tarea, proyectoColor, justCompleted = false, isOverlay = false, selected = false, onToggleSelect, hasAnySelection = false }) => {
  const etiquetas = tarea.etiquetas || [];
  const etiquetasInline = etiquetas.slice(0, 2);
  const etiquetasExtras = etiquetas.length - etiquetasInline.length;
  const subtareasTotal = tarea.subtareas_total || 0;
  const subtareasHechas = tarea.subtareas_completadas || 0;
  const comentarios = tarea.comentarios_count || 0;
  const tieneAsignados = (tarea.asignados?.length || 0) > 0;
  const codigo = tarea.codigo || `${tarea.proyecto_slug || '?'}-${tarea.numero}`;
  const color = proyectoColor || '#06b6d4';
  const cat = tarea.estado?.categoria;
  const isDone = cat === 'hecho' || cat === 'cancelado';
  const tieneFooterIzq = subtareasTotal > 0 || comentarios > 0 || !!tarea.fecha_vencimiento;
  const tieneFooter = tieneFooterIzq || tieneAsignados;

  return (
    <div
      className={`group relative bg-white rounded-lg border overflow-hidden transition-all duration-150 ${
        selected ? 'border-cyan-400 ring-2 ring-cyan-200/50' : 'border-slate-200/80'
      } ${
        isOverlay
          ? 'cursor-grabbing'
          : 'cursor-grab active:cursor-grabbing hover:border-slate-300 hover:shadow-[0_2px_8px_-2px_rgba(15,23,42,0.08)]'
      } ${isDone ? 'opacity-60' : ''}`}
    >
      <div className="px-3 py-2.5">
        {/* Top row: checkbox + código + etiquetas inline + chip bloqueada + prioridad */}
        <div className="flex items-center gap-1.5 mb-1.5">
          {/* Checkbox para seleccion bulk. Visible en hover o siempre si hay
              alguna seleccion activa en el board. stopPropagation para que el
              click no inicie drag ni abra el slide-over. */}
          {onToggleSelect && !isOverlay && (
            <button
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onToggleSelect(tarea.id);
              }}
              className={`flex items-center justify-center w-3.5 h-3.5 rounded border transition-all flex-shrink-0 ${
                selected
                  ? 'bg-cyan-500 border-cyan-500 text-white'
                  : 'border-slate-300 hover:border-cyan-400 bg-white text-transparent'
              } ${selected || hasAnySelection ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
              aria-label={selected ? 'Deseleccionar tarea' : 'Seleccionar tarea'}
            >
              {selected && (
                <svg className="w-2.5 h-2.5" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6.5L4.5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
          )}
          <span className="font-mono text-[9px] tracking-widest text-slate-400 uppercase flex-shrink-0">
            {codigo}
          </span>
          {etiquetasInline.map((e) => (
            <EtiquetaChip key={e.id} etiqueta={e} />
          ))}
          {etiquetasExtras > 0 && (
            <span
              className="text-[9px] font-medium text-slate-400 tabular-nums px-1"
              title={etiquetas.slice(2).map((e) => e.nombre).join(', ')}
            >
              +{etiquetasExtras}
            </span>
          )}
          {tarea.hito && <HitoChip hito={tarea.hito} />}
          <span className="flex-1" />
          {!isDone && (tarea.esta_bloqueada || (tarea.bloqueadoras_pendientes?.length > 0)) && (
            <span
              title={`Arranca cuando se cierre ${(tarea.bloqueadoras_pendientes || []).map((b) => b.codigo).join(', ')}`}
              className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-indigo-50 text-indigo-600 text-[9px] font-semibold"
            >
              <Hourglass size={9} strokeWidth={2.5} />
              En espera
            </span>
          )}
          <PriorityBadge prioridad={tarea.prioridad} />
        </div>

        {/* Título */}
        <h4 className={`text-sm font-medium leading-snug line-clamp-2 ${
          isDone ? 'text-slate-500' : 'text-slate-800'
        } ${tieneFooter ? 'mb-2' : ''}`}>
          {tarea.titulo}
        </h4>

        {/* Footer (oculto cuando no hay subtareas/comentarios/fecha ni asignado) */}
        {tieneFooter && (
          <div className="flex items-center justify-between gap-2 mt-3">
            <div className="flex items-center gap-2 text-slate-400">
              {subtareasTotal > 0 && (
                <span className="inline-flex items-center gap-0.5 text-[10px] tabular-nums">
                  <CheckSquare size={10} strokeWidth={2} />
                  {subtareasHechas}/{subtareasTotal}
                </span>
              )}
              {comentarios > 0 && (
                <span className="inline-flex items-center gap-0.5 text-[10px] tabular-nums">
                  <MessageSquare size={10} strokeWidth={2} />
                  {comentarios}
                </span>
              )}
              <FechaChip fecha={tarea.fecha_vencimiento} neutra={isDone || !!tarea.congelada} />
            </div>

            <AsignadosStack asignados={tarea.asignados} color={color} size={20} max={2} />
          </div>
        )}
      </div>

      <AnimatePresence>
        {justCompleted && !isOverlay && <Celebracion />}
      </AnimatePresence>
    </div>
  );
};

// --- Tarea recien completada: un barrido verde que nace del centro, el check
//     que cae en su sitio y una onda que se abre y se apaga. Arranca cuando la
//     tarjeta ya va terminando de aterrizar en Hecho.
const Celebracion = () => (
  <motion.div
    initial={{ clipPath: 'circle(0% at 50% 50%)' }}
    animate={{ clipPath: 'circle(75% at 50% 50%)' }}
    exit={{ opacity: 0, transition: { duration: 0.3 } }}
    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1], delay: 0.18 }}
    className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none bg-emerald-500"
  >
    <motion.span
      initial={{ scale: 0.6, opacity: 0 }}
      animate={{ scale: 2.6, opacity: [0, 0.7, 0] }}
      transition={{ duration: 0.8, ease: 'easeOut', delay: 0.4 }}
      className="absolute w-12 h-12 rounded-full border-2 border-white/80"
    />
    <motion.span
      initial={{ scale: 0, rotate: -30 }}
      animate={{ scale: 1, rotate: 0 }}
      transition={{ type: 'spring', stiffness: 520, damping: 17, delay: 0.3 }}
      className="relative inline-flex items-center justify-center w-12 h-12 rounded-full bg-white text-emerald-600 shadow-lg"
    >
      <Check size={26} strokeWidth={3.5} />
    </motion.span>
  </motion.div>
);

// --- Hueco que deja la tarjeta en su columna mientras se arrastra. Guarda el
//     alto exacto de la tarjeta (nada salta al agarrarla) y se cierra cuando la
//     tarjeta pasa sobre otra columna: se lee que ya se fue de aqui.
const Hueco = React.forwardRef(({ alto, colapsado, reduce }, ref) => (
  <motion.div
    ref={ref}
    initial={false}
    animate={{
      height: colapsado ? 0 : alto,
      marginBottom: colapsado ? 0 : 6,
      borderWidth: colapsado ? 0 : 1.5,
      opacity: colapsado ? 0 : 1,
    }}
    transition={reduce ? { duration: 0 } : RESORTE_HUECO}
    className="rounded-lg border-dashed border-slate-300 bg-slate-100/60"
  />
));

// --- Envoltura animada de la tarjeta en su columna.
//  - layout: si cambia de lugar (otra tarjeta se fue, un filtro), se desliza.
//  - layoutId: si cambia de columna sin arrastre (seleccion multiple, slide-over),
//    vuela de una columna a la otra.
//  - aterrizaje: al soltarla, despega desde donde quedo la tarjeta flotante (con
//    su inclinacion y su escala) y se asienta en su lugar.
//  - entrada: las tarjetas que aparecen (carga, tarea nueva, filtro) suben con
//    un fundido; en la carga, en cascada columna por columna.
const TarjetaAnimada = ({ layoutId, entrada, aterrizaje, reduce, children }) => {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotate = useMotionValue(0);
  const scale = useMotionValue(1);
  const boxShadow = useMotionValue(SOMBRA_NULA);
  // El giro de la tarjeta flotante es alrededor del punto donde se agarro; el
  // aterrizaje sale de ese mismo eje para que el primer cuadro calce exacto.
  const origen = useRef(aterrizaje?.origen || '50% 50%');

  useLayoutEffect(() => {
    if (!aterrizaje || reduce || !ref.current) return undefined;
    // Se mide la caja contenedora (sin transformar): asi la cuenta da igual
    // aunque el efecto se repita (StrictMode) con la tarjeta ya desplazada.
    const r = (ref.current.parentElement || ref.current).getBoundingClientRect();
    x.set(aterrizaje.left - r.left);
    y.set(aterrizaje.top - r.top);
    rotate.set(aterrizaje.rotate || 0);
    scale.set(aterrizaje.scale || 1);
    boxShadow.set(SOMBRA_ALZADA);
    const controles = [
      animate(x, 0, RESORTE_TARJETA),
      animate(y, 0, RESORTE_TARJETA),
      animate(rotate, 0, RESORTE_TARJETA),
      animate(scale, 1, RESORTE_TARJETA),
      animate(boxShadow, SOMBRA_NULA, { duration: 0.35, ease: 'easeOut' }),
    ];
    return () => controles.forEach((c) => c.stop());
    // Solo al montar: es la tarjeta recien puesta en su columna.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <motion.div
      ref={ref}
      layout={reduce ? false : 'position'}
      layoutId={reduce ? undefined : layoutId}
      transition={RESORTE_TARJETA}
      className="rounded-lg"
      style={{ x, y, rotate, scale, boxShadow, transformOrigin: origen.current }}
    >
      <motion.div
        initial={entrada != null && !reduce ? { opacity: 0, y: 10, scale: 0.97 } : false}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ ...RESORTE_TARJETA, delay: entrada || 0 }}
      >
        {children}
      </motion.div>
    </motion.div>
  );
};

// --- TaskCard "real": arrastrable. Mientras se arrastra deja el hueco; el
//     contenido viaja en la TarjetaFlotante.
const TaskCard = ({
  tarea, onClick, proyectoColor, justCompleted = false, selected = false, onToggleSelect,
  hasAnySelection = false, layoutKey = 0, entrada = null, aterrizaje = null,
  altoHueco = 72, huecoColapsado = false, reduce = false,
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `tarea-${tarea.id}`,
    data: { tipo: 'tarea', tarea },
  });

  if (isDragging) {
    return <Hueco ref={setNodeRef} alto={altoHueco} colapsado={huecoColapsado} reduce={reduce} />;
  }

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      data-tarjeta-id={tarea.id}
      className="mb-1.5"
      onClick={(e) => {
        if (e.detail === 0) return;
        onClick?.(tarea);
      }}
    >
      <TarjetaAnimada
        layoutId={`tarea-${tarea.id}-${layoutKey}`}
        entrada={entrada}
        aterrizaje={aterrizaje}
        reduce={reduce}
      >
        <TaskCardBody
          tarea={tarea}
          proyectoColor={proyectoColor}
          justCompleted={justCompleted}
          selected={selected}
          onToggleSelect={onToggleSelect}
          hasAnySelection={hasAnySelection}
        />
      </TarjetaAnimada>
    </div>
  );
};

// --- Tarjeta que viaja con el puntero mientras se arrastra. Va en un portal al
//     body (position fixed sin ancestros transformados que la desplacen) y se
//     mueve con motion values: ni un render de React por cada movimiento.
//     Al agarrarla se despega (crece y proyecta sombra) y se inclina segun la
//     velocidad con que se la lleva, girando sobre el punto donde se agarro.
export const TarjetaFlotante = ({ arrastre, x, y, inclinacion, proyectoColor, reduce }) => {
  if (!arrastre?.rect) return null;
  const { rect, origen, tarea } = arrastre;
  return createPortal(
    <div
      className="pointer-events-none"
      style={{ position: 'fixed', left: rect.left, top: rect.top, width: rect.width, zIndex: 1000 }}
    >
      <motion.div style={{ x, y, rotate: reduce ? 0 : inclinacion, transformOrigin: origen }}>
        <motion.div
          initial={{ scale: 1, boxShadow: SOMBRA_NULA }}
          animate={{ scale: reduce ? 1 : ESCALA_ALZADA, boxShadow: SOMBRA_ALZADA }}
          transition={reduce ? { duration: 0 } : RESORTE_ALZAR}
          className="rounded-lg"
          style={{ transformOrigin: origen }}
        >
          <TaskCardBody tarea={tarea} proyectoColor={proyectoColor} isOverlay />
        </motion.div>
      </motion.div>
    </div>,
    document.body
  );
};

export default TaskCard;
